"use client";

/**
 * Homes for sale: the units still open on the builder's own developments
 * (off-plan, paid by stages) — a table on a wide screen, a card each on a
 * phone — and the finished homes already listed in Property, each linking
 * to its own listing page. Prices show in the website's other currency too,
 * and a house's plot in the unit it was measured in.
 */

import { BedDouble, LandPlot, Ruler } from "lucide-react";
import type { BuilderListing, BuilderUnit } from "@/lib/config/builderSite";
import { askAbout, floorLabel, foreignText, ugx } from "../theme";
import HomePrice from "./HomePrice";

export default function HomesSection({ units, listings }: { units: BuilderUnit[]; listings: BuilderListing[] }) {
  return (
    <div className="grid gap-10">
      {units.length > 0 && (
        <>
          {/* Wide screens: one table. */}
          <div className="hidden overflow-x-auto rounded-xl border border-[var(--b-line)] bg-white md:block">
            <table className="w-full text-sm">
              <thead className="bg-[var(--b-ground)] text-left text-xs uppercase tracking-wide text-[var(--b-muted)]">
                <tr>
                  <th className="px-4 py-3 font-bold">Home</th>
                  <th className="px-4 py-3 font-bold">Project</th>
                  <th className="px-4 py-3 font-bold">Bedrooms</th>
                  <th className="px-4 py-3 text-right font-bold">Floor area</th>
                  <th className="px-4 py-3 text-right font-bold">Price</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={`${u.project}-${u.label}`} className="border-t border-[var(--b-line)]">
                    <td className="px-4 py-3 font-semibold">
                      {u.label}
                      <span className="block text-xs font-normal text-[var(--b-muted)]">{[u.block, floorLabel(u.floor)].filter((x) => x && x !== "—").join(" · ")}</span>
                    </td>
                    <td className="px-4 py-3">{u.projectName}</td>
                    <td className="px-4 py-3">{u.bedrooms ?? "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {u.areaM2} m²
                      {u.plot && <span className="block text-xs text-[var(--b-muted)]">Plot {u.plot}</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">
                      <HomePrice price={u.price} foreign={u.foreign} reserved={u.status === "reserved"} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.status === "available" && (
                        <button type="button" onClick={() => askAbout(u.project, u.label)} className="font-bold text-[var(--b-accent-ink)] hover:underline">
                          Ask about it
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Phones: a card each. */}
          <ul className="grid gap-3 md:hidden">
            {units.map((u) => (
              <li key={`${u.project}-${u.label}`} className="grid gap-2 rounded-xl border border-[var(--b-line)] bg-white p-4">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-extrabold">{u.label}</span>
                  <span className="font-bold tabular-nums">
                    <HomePrice price={u.price} foreign={u.foreign} reserved={u.status === "reserved"} />
                  </span>
                </div>
                <p className="text-sm text-[var(--b-muted)]">{[u.projectName, u.block, floorLabel(u.floor)].filter((x) => x && x !== "—").join(" · ")}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {u.bedrooms !== undefined && (
                    <span className="inline-flex items-center gap-1.5">
                      <BedDouble className="h-4 w-4 text-[var(--b-muted)]" /> {u.bedrooms} bed
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    <Ruler className="h-4 w-4 text-[var(--b-muted)]" /> {u.areaM2} m²
                  </span>
                  {u.plot && (
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <LandPlot className="h-4 w-4 text-[var(--b-muted)]" /> Plot {u.plot}
                    </span>
                  )}
                  {u.status === "available" && (
                    <button type="button" onClick={() => askAbout(u.project, u.label)} className="ml-auto font-bold text-[var(--b-accent-ink)]">
                      Ask about it
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {listings.length > 0 && (
        <div className="grid gap-4">
          <h3 className="text-xl font-extrabold">{units.length > 0 ? "Ready to move into" : "Our listings"}</h3>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {listings.map((l) => (
              <li key={l.slug} className="min-w-0">
                <a href={`/property/${l.slug}`} className="grid h-full overflow-hidden rounded-xl border border-[var(--b-line)] bg-white hover:border-[var(--b-ink)]">
                  {l.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={l.image} alt="" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover" />
                  ) : (
                    <div className="aspect-[4/3] bg-[var(--b-ground)]" />
                  )}
                  <div className="grid gap-1 p-4">
                    <span className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--b-accent-ink)]">{l.forRent ? "To rent" : "For sale"}</span>
                    <span className="font-extrabold leading-snug">{l.name}</span>
                    <span className="text-sm text-[var(--b-muted)]">
                      {l.foreign?.first ? foreignText(l.foreign) : l.price > 0 ? ugx(l.price) : "Ask for the price"}
                      {l.bedrooms ? ` · ${l.bedrooms} bed` : ""}
                    </span>
                    {l.foreign && (l.foreign.first ? l.price > 0 : true) && (
                      <span className="text-sm text-[var(--b-muted)]">{l.foreign.first ? ugx(l.price) : foreignText(l.foreign)}</span>
                    )}
                    {l.plot && <span className="text-xs text-[var(--b-muted)]">Plot {l.plot}</span>}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
